/**
 * Business → Strategy (#6232 Phase 3): the strategy report's card.
 *
 * One report per business. This card shows where it stands (none yet /
 * generating, with how long it has been running / ready with its
 * updated-at), says what the report gives, triggers Generate / Update from
 * the button at its bottom right (#6671: no confirm; whether call actions
 * are reviewed is the agency's concern on the dashboard, so nothing about
 * them shows here), and
 * links to the report itself, which lives in /reports. The standing inputs
 * (KPI targets, goals, biggest challenge) render alongside on the same tab.
 * The runs query polls every 4s while a run is queued or running.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, FileText, RefreshCcw, Sparkles, Compass } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/glitch-ui/spinner';
import { strategyDocRunsQueryOptions } from '@/lib/strategy/query-options';
import { createStrategyDocRun, type StrategyDocRunSummary } from '@/lib/strategy/api';

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

/** The one live report: the newest non-archived GENERATED run. Polls every
 * 4s while a run is queued or running; ``idleIntervalMs`` adds a slow poll
 * between runs for surfaces that must notice a run started elsewhere (the
 * Reports nav item). */
export function useStrategyReport(orgId: number, businessId: number, opts: { idleIntervalMs?: number } = {}) {
  const runsQuery = useQuery({
    ...strategyDocRunsQueryOptions(orgId, businessId),
    refetchInterval: (query) => {
      const rs = query.state.data?.runs ?? [];
      return rs.some((r) => r.status === 'queued' || r.status === 'running') ? 4000 : (opts.idleIntervalMs ?? false);
    },
    enabled: orgId > 0 && businessId > 0,
  });
  const generated = (runsQuery.data?.runs ?? []).filter((r) => r.source === 'generated');
  const inFlight = generated.find((r) => r.status === 'queued' || r.status === 'running') ?? null;
  const report: StrategyDocRunSummary | null = generated.find((r) => r.status === 'succeeded') ?? null;
  const latestFailed = generated[0]?.status === 'failed' ? generated[0] : null;
  return { runsQuery, report, inFlight, latestFailed };
}

export function StrategyReportPanel({ orgId, businessId }: { orgId: number; businessId: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { runsQuery, report, inFlight, latestFailed } = useStrategyReport(orgId, businessId);

  const generateMutation = useMutation({
    mutationFn: () => createStrategyDocRun(orgId, businessId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['strategy'] }),
  });


  // The failure notes share one quiet shape: a thin-bordered tinted line under
  // the heading, full width, rather than a heavy banner.
  const noteClass = 'mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-sm';

  return (
    <div className='rounded-lg border bg-white p-6 shadow-sm'>
      <div className='flex items-start gap-4'>
        <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#290BD9] bg-opacity-10'>
          <Compass className='h-5 w-5 text-[#290BD9]' />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='flex items-baseline justify-between gap-4'>
            <h3 className='text-lg font-semibold text-gray-900'>Strategy Report</h3>
            {report && !inFlight && (
              <span className='flex-shrink-0 text-sm text-gray-500'>
                Last updated {formatDateTime(report.markdown_updated_at ?? report.finished_at)}
              </span>
            )}
          </div>
          <div className='mt-1 text-sm text-gray-500'>
            {inFlight ? (
              'Your report is being generated. This can take 2-3 minutes.'
            ) : runsQuery.isLoading ? (
              <span className='flex items-center gap-2'>
                <Spinner size='sm' /> Loading...
              </span>
            ) : (
              'What to change, what to add and where the budget should go.'
            )}
          </div>
        </div>
      </div>

      {latestFailed && !inFlight && (
        <div className={`${noteClass} border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C]`}>
          <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0' />
          <span>
            The last generation failed{latestFailed.error_text ? `: ${latestFailed.error_text}` : '.'} You can try
            again.
          </span>
        </div>
      )}
      {generateMutation.isError && (
        <div className={`${noteClass} border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C]`}>
          <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0' />
          <span>{(generateMutation.error as Error).message}</span>
        </div>
      )}

      <div className='mt-4 flex justify-end gap-2'>
        {/* Reading the report is the thing to do once one exists (#6718), so
            View wears the primary and Update steps back to an outline. With no
            report there is nothing to view and Generate is the one primary. */}
        {report && (
          <Button
            className='h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white'
            onClick={() => navigate({ to: '/reports/$reportId', params: { reportId: 'strategy' } })}
          >
            <FileText className='mr-2 h-4 w-4' /> View report
          </Button>
        )}
        {/* While a run is queued or running the button itself says so:
            spinner and "Generating", disabled, in place of a status pill. */}
        <Button
          variant={report ? 'outline' : undefined}
          className={report ? 'h-9 text-gray-900' : 'h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white'}
          disabled={runsQuery.isLoading || inFlight !== null || generateMutation.isPending}
          onClick={() => generateMutation.mutate()}
        >
          {inFlight || generateMutation.isPending ? (
            <>
              <Spinner size='sm' className='mr-2' /> Generating
            </>
          ) : report ? (
            <>
              <RefreshCcw className='mr-2 h-4 w-4' /> Update report
            </>
          ) : (
            <>
              <Sparkles className='mr-2 h-4 w-4' /> Generate report
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
